import { useCallback, useContext, useEffect, useState } from 'react';
import { Platform, Pressable, ScrollView, StyleSheet, useWindowDimensions } from 'react-native';
import { BasicScrollView } from "@/components/BasicScrollView";
import { View } from '@/components/Themed';
import Head from 'expo-router/head';
import * as AppUtils from '@/utilities/AppUtils';
import * as AppConsts from '@/utilities/AppConsts';
import * as AppUtilities from '@/utilities/AppUtilities';
import { CustomText, CustomTextStyles } from '@/components/CustomText';
import { Button, Checkbox, Fieldset, Hourglass, Panel, TextInput } from 'react95-native-rabbl';
import { AppText } from '@/components/AppText';
import type { RootState } from '@/state/store';
import { useSelector, useDispatch } from 'react-redux';
import { setLoggedIn, removeSearchResultByID } from '@/state/user';

import {
  ExternalPathString,
  Link,
  RelativePathString,
  router,
  useRouter,
  useLocalSearchParams,
} from 'expo-router';

import Footer from '@/components/Footer';
import * as AppConstants from '@/utilities/AppConstants';
import Profile from '@/components/Profile';
import { AppContext } from '@/components/AppContext';
import { Image } from 'expo-image';
import { Community, CommunityInfo, UNKNOWN_COMMUNITY } from '@/types/community';
import { RabblLink } from '@/components/RabblLink';
import { setData } from '@/state/user';
import { CustomDivider } from "@/components/CustomDivider";
import { RabblStats } from '@/components/RabblStats';
import { Collaboration, CollaborationStatus } from '@/types/collaboration';
import { ExternalLink } from '@/components/ExternalLink';
import { Invitecode } from '@/types/invitecode';
import { ALL_GENDERS_BITFLAGS, Gender, includesGenderMan, includesGenderWoman } from '@/types/user_info';
import { Meta } from '@/components/Meta';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    paddingTop: 10,
    gap: 20,
    backgroundColor: 'transparent',
    alignItems: 'center',
  },
  image: {
    width: 200,
    height: 200,
  },
  bottomLeft: {
    position: 'absolute',
    bottom: 30,
    left: 30,
    backgroundColor: 'transparent',
  },
  bottomRight: {
    position: 'absolute',
    bottom: 30,
    right: 30,
    backgroundColor: 'transparent',
  },
  imageWrapper: {
    backgroundColor: 'transparent',
  },
  wrapper: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  decisionImage: {
    width: 64,
    height: 64,
  },
  decisionWrapper: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    width: '100%',
    gap: 40,
    position: 'absolute',
    bottom: 0,
  },
  avatar: {
    width: 48,
    height: 48,
    borderRadius: 34,
    backgroundColor: 'transparent',
  },
  largeAvatar: {
    width: 100,
    height: 100,
    borderRadius: 50,
    backgroundColor: 'transparent',
  },
  header: {
    padding: 20,
    backgroundColor: 'transparent',
    alignItems: 'center',
    gap: 20,
  },
  emptyContainer: {
    padding: 20,
    backgroundColor: 'transparent',
    alignItems: 'center',
    gap: 20,
  },
  sendButton: {
    marginRight: 2,
    marginBottom: 2,
  },
  panel: {
    padding: 20,
    gap: 20,
    alignItems: 'center',
    backgroundColor: 'transparent',
  },
  content: {
    padding: 20,
    gap: 20,
    alignItems: 'center',
    backgroundColor: 'transparent',
  },
  buttonWrapper: {
    flexDirection: 'row',
  },
  flexRowGapVerticalCenter: {
    flexDirection: 'row',
    gap: 10,
    alignItems: "center",
    backgroundColor: "transparent",
    flexWrap: "wrap",
  },
  flexRowGap: {
    flexDirection: 'row',
    gap: 10,
    alignItems: "center",
    backgroundColor: "transparent",
    flexWrap: "wrap",
  },
  editImage: {
    width: 32,
    height: 32,
  },
  editImageWrapper: {
    position: 'absolute',
    right: 0,
    bottom: 0,
  },
  collabRow: {
    flexDirection: "row",
    gap: 20,
    backgroundColor: 'transparent',
    alignItems: 'center',
  },
  collabRowWrapper: {
    gap: 20,
    backgroundColor: 'transparent',
  },
});

interface InviteCodesProps {
  manage_community_id: string,
  initialInviteCodes: Invitecode[],
}

function InviteCodes({ manage_community_id, initialInviteCodes } : InviteCodesProps) {
  const [invitecodes, setInvitecodes] = useState<Invitecode[]>(initialInviteCodes);
  const { showSpinner, hideSpinner } = useContext(AppContext);

  const saveInviteCodes = useCallback(async () => {

    for (const ic of invitecodes) {
      if (ic.code.trim().length == 0) {
        AppUtils.showInformationalToast("Error: One or more of the codes are blank");
        return;
      } else if (ic.genders == 0) {
        AppUtils.showInformationalToast("Error: One or more of the codes disallow both men & women");
        return;
      }
    }

    try {
      showSpinner();

      const response = await AppUtilities.fetchAuthorized(
        "InviteCodes.saveInviteCodes",
        "/rabbl/api/set_rabbl_invitecodes",
        "POST",
        {
          id: manage_community_id,
          codes: invitecodes,
        }
      );

      const updatedInviteCodes: Invitecode[] = ((response as any).body as any).invitecodes;
      setInvitecodes(updatedInviteCodes);
      AppUtils.showInformationalToast("Successfully updated invite codes");

    } catch (e : any) {
      AppUtilities.handleException(e, "InviteCodes.saveInviteCodes");
      AppUtils.showErrorToast(AppUtilities.getErrors(e), () => saveInviteCodes(), true);
    } finally {
      hideSpinner();
    }
  }, [manage_community_id, invitecodes]);

  return (
    <>
      {
        invitecodes.map((ic, index) => (
          <View key={"ic" + index} style={{ backgroundColor: "transparent" }}>
            <Fieldset label="Invite code" style={{ flexDirection: "row" }}>
              <View style={{ backgroundColor: "transparent", gap: 10 }}>
                <TextInput
                  value={ic.code}
                  onChangeText={(c) => {
                    const newInviteCodes = [...invitecodes];
                    newInviteCodes[index].code = c;
                    setInvitecodes(newInviteCodes);
                  }}
                  placeholder="Code"
                  style={{}}
                  returnKeyType="next"
                />
                <View style={{ gap: 10, flexDirection: "row", alignItems: "center", justifyContent: "center", backgroundColor: "transparent" }}>
                  <View style={{ backgroundColor: "transparent" }}>
                    <Checkbox
                      status={includesGenderMan(ic.genders) ? 'checked' : 'unchecked'}
                      onPress={() => {
                        const newInviteCodes = [...invitecodes];
                        newInviteCodes[index].genders ^= Gender.GENDER_MAN;
                        setInvitecodes(newInviteCodes);
                      }}
                      label={"Men"}
                    />
                  </View>
                  <View style={{ backgroundColor: "transparent" }}>
                    <Checkbox
                      status={includesGenderWoman(ic.genders) ? 'checked' : 'unchecked'}
                      onPress={() => {
                        const newInviteCodes = [...invitecodes];
                        newInviteCodes[index].genders ^= Gender.GENDER_WOMAN;
                        setInvitecodes(newInviteCodes);
                      }}
                      label={"Women"}
                    />
                  </View>
                </View>
                <AppText>Current uses: {ic.currentuses}</AppText>
                <AppText>Maximum uses:</AppText>
                <TextInput
                  value={ic.maxuses <= 0 ? "" : "" + ic.maxuses}
                  onChangeText={(c) => {
                    const newInviteCodes = [...invitecodes];
                    if (c.length == 0) {
                      newInviteCodes[index].maxuses = 0;
                    } else {
                      const max = parseInt(c);
                      if (!Number.isNaN(max)) {
                        newInviteCodes[index].maxuses = max;
                      }
                    }
                    setInvitecodes(newInviteCodes);
                  }}
                  placeholder="Unlimited"
                  style={{}}
                  returnKeyType="next"
                  inputMode='numeric'
                  keyboardType='number-pad'
                />
                <View style={{ backgroundColor: "transparent", flexDirection: "row", justifyContent: "center" }}>
                  <Button onPress={() => {
                    setInvitecodes(invitecodes.toSpliced(index, 1));
                  }}>Delete</Button>
                </View>
              </View>
            </Fieldset>
          </View>
        ))
      }
      <Button
        onPress={() =>
          setInvitecodes([
            ...invitecodes,
            {
              id: -1,
              code: "",
              code_name: "",
              code_type: 1,
              currentuses: 0,
              genders: ALL_GENDERS_BITFLAGS,
              maxuses: 0,
            }
          ])
        }
      >Add Code</Button>
      <Button onPress={saveInviteCodes}><AppText style={{ fontWeight: "bold" }}>Save codes</AppText></Button>
    </>
  );
}

export default function RabblAdmin() {
  const { showSpinner, hideSpinner } = useContext(AppContext);
  const { manage_community_id } = useLocalSearchParams<{ manage_community_id: string }>();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const router = useRouter();
  const community = useSelector((state: RootState) => state.user.creating_community);
  const community_info = useSelector((state: RootState) => state.user.current_community_info);
  const [collaborations, setCollaborations] = useState<Collaboration[]>([]);
  const [initialInviteCodes, setInitialInvitecodes] = useState<Invitecode[]>([]);
  const {height, width} = useWindowDimensions();

  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      AppUtilities.info("RabblAdmin.loadInitialData started");
      const response = await AppUtilities.fetchAuthorized(
        "RabblAdmin.loadInitialData",
        "/rabbl/api/adminrabbl",
        "POST",
        {
          id: manage_community_id,
        }
      );
      AppUtilities.info("RabblAdmin.loadInitialData finished:");
      AppUtilities.dir((response as any).body);

      const communityResult: Community = ((response as any).body as any).community;
      const community_info: CommunityInfo = ((response as any).body as any).community_info;
      const collaborationsResult: Collaboration[] = ((response as any).body as any).collaborations;
      const inviteCodes: Invitecode[] = ((response as any).body as any).invitecodes;
      dispatch(setData({
        body: {
          creating_community: communityResult,
          community_info: community_info,
        }
      }));
      setCollaborations(collaborationsResult);
      setInitialInvitecodes(inviteCodes);

    } catch (e) {
      AppUtilities.handleException(e, "RabblAdmin.loadInitialData");
      setError(AppUtilities.getErrors(e));
    } finally {
      setLoading(false);
    }
  }, [manage_community_id]);

  useEffect(() => {
    if (manage_community_id) {
      loadInitialData();
    }
  }, [manage_community_id]);

  const cancelCollab = useCallback(async (collaboration: Collaboration) => {
    try {
      showSpinner();
      setError('');

      const response = await AppUtilities.fetchAuthorized(
        "RabblAdmin.cancelCollab",
        "/rabbl/api/cancelcollab",
        "POST",
        {
          collaboration_uniqueid: collaboration.uniqueid,
        }
      );

      AppUtils.showInformationalToast("Collab canceled");

      loadInitialData();

    } catch (e : any) {
      AppUtilities.handleException(e, "RabblAdmin.cancelCollab");
      AppUtils.showErrorToast(AppUtilities.getErrors(e), () => cancelCollab(collaboration), true);
    } finally {
      hideSpinner();
    }
  }, [loadInitialData]);

  const approveCollab = useCallback(async (collaboration: Collaboration) => {
    try {
      showSpinner();
      setError('');

      const response = await AppUtilities.fetchAuthorized(
        "RabblAdmin.approveCollab",
        "/rabbl/api/approvecollab",
        "POST",
        {
          collaboration_uniqueid: collaboration.uniqueid,
        }
      );

      AppUtils.showInformationalToast("Collab approved");

      loadInitialData();

    } catch (e : any) {
      AppUtilities.handleException(e, "RabblAdmin.approveCollab");
      AppUtils.showErrorToast(AppUtilities.getErrors(e), () => approveCollab(collaboration), true);
    } finally {
      hideSpinner();
    }
  }, [loadInitialData]);

  const changeInviteOnly = useCallback(async (newInviteOnly: boolean) => {
    try {
      showSpinner();

      const response = await AppUtilities.fetchAuthorized(
        "RabblAdmin.changeInviteOnly",
        "/rabbl/api/set_rabbl_inviteonly",
        "POST",
        {
          id: manage_community_id,
          new_invite_only: newInviteOnly,
        }
      );

      dispatch(setData(response));

      if (newInviteOnly) {
        AppUtils.showInformationalToast("Successfully changed to invite only");
      } else {
        AppUtils.showInformationalToast("Successfully changed to public");
      }

    } catch (e : any) {
      AppUtilities.handleException(e, "RabblAdmin.approchangeInviteOnlyveCollab");
      AppUtils.showErrorToast(AppUtilities.getErrors(e), () => changeInviteOnly(newInviteOnly), true);
    } finally {
      hideSpinner();
    }
  }, [manage_community_id]);

  if (loading || !community || community.id == -1) {
    return (
      <View style={AppConstants.STYLES.container2}>
        <Hourglass size={AppConstants.LARGE_HOURGLASS_SIZE} />
      </View>
    );
  }

  if (error.length > 0) {
    return (
      <View style={AppConstants.STYLES.container2}>
        <CustomText>Failed to load</CustomText>
        <AppText>{error}</AppText>
        <Button onPress={loadInitialData}>Retry</Button>
      </View>
    );
  }

  return (
    <BasicScrollView center={true}>
      <Meta title={"Manage"} />
      
      <Panel variant='raised' elevation={4}>
        <View style={[styles.panel, { maxWidth: AppConsts.MAX_WIDTH_COMFORTABLE_READING }]}>
          <View style={styles.flexRowGapVerticalCenter}>
            <CustomText>{community.name}</CustomText>
            <Link href={{ pathname: "/rabblname", params: { id: community.id } }} asChild={AppUtilities.isWeb()}>
              <Image
                style={styles.editImage}
                source={require('@/assets/images/gears32x32.png')}
              />
            </Link>
          </View>
          <AppText>{community.approved ? "Fully approved and live" : "Waiting for approval"}</AppText>
          <View style={styles.flexRowGapVerticalCenter}>
            <Image
              source={AppUtils.getCommunityImage(community)}
              alt={community.name}
              contentFit="contain"
              style={styles.image}
            />
            <Link href={{ pathname: "/rabblvisuals", params: { id: community.id } }} style={styles.editImageWrapper} asChild={AppUtilities.isWeb()}>
              <Image
                style={styles.editImage}
                source={require('@/assets/images/gears32x32.png')}
              />
            </Link>
          </View>
          <View style={styles.flexRowGapVerticalCenter}>
            <AppText>Rabbl Link:</AppText>
            <RabblLink community={community}>
              <AppText style={CustomTextStyles.link}>{AppUtils.getCommunityURL(community)}/</AppText>
            </RabblLink>
            <Link href={{ pathname: "/rabbllink", params: { id: community.id } }} asChild={AppUtilities.isWeb()}>
              <Image
                style={styles.editImage}
                source={require('@/assets/images/gears32x32.png')}
              />
            </Link>
          </View>
          <View style={styles.flexRowGapVerticalCenter}>
            <AppText>{"Description: " + community.details}</AppText>
            <Link href={{ pathname: "/rabbldetails", params: { id: community.id } }} asChild={AppUtilities.isWeb()}>
              <Image
                style={styles.editImage}
                source={require('@/assets/images/gears32x32.png')}
              />
            </Link>
          </View>
          <View style={styles.flexRowGapVerticalCenter}>
            <AppText>External Link: </AppText>
            {
              community.external_link != null && community.external_link.length > 0 ?
                  <ExternalLink href={community.external_link} target="_blank" style={CustomTextStyles.link}>{community.external_link}</ExternalLink>
                :
                  <AppText>None</AppText>
            }
            <Link href={{ pathname: "/rabbldetails", params: { id: community.id } }} asChild={AppUtilities.isWeb()}>
              <Image
                style={styles.editImage}
                source={require('@/assets/images/gears32x32.png')}
              />
            </Link>
          </View>
          <View style={styles.flexRowGapVerticalCenter}>
            <AppText>Cover charge for men: {AppUtils.formatPublicPrice(community.price_men)}</AppText>
            <Link href={{ pathname: "/covercharge", params: { id: community.id } }} asChild={AppUtilities.isWeb()}>
              <Image
                style={styles.editImage}
                source={require('@/assets/images/gears32x32.png')}
              />
            </Link>
          </View>
          <View style={styles.flexRowGapVerticalCenter}>
            <AppText>Cover charge for women: {AppUtils.formatPublicPrice(community.price_women)}</AppText>
            <Link href={{ pathname: "/covercharge", params: { id: community.id } }} asChild={AppUtilities.isWeb()}>
              <Image
                style={styles.editImage}
                source={require('@/assets/images/gears32x32.png')}
              />
            </Link>
          </View>
          <View style={styles.flexRowGapVerticalCenter}>
            <AppText>First free men memberships: {community.first_free_men}</AppText>
            <Link href={{ pathname: "/covercharge", params: { id: community.id } }} asChild={AppUtilities.isWeb()}>
              <Image
                style={styles.editImage}
                source={require('@/assets/images/gears32x32.png')}
              />
            </Link>
          </View>
          <View style={styles.flexRowGapVerticalCenter}>
            <AppText>First free women memberships: {community.first_free_women}</AppText>
            <Link href={{ pathname: "/covercharge", params: { id: community.id } }} asChild={AppUtilities.isWeb()}>
              <Image
                style={styles.editImage}
                source={require('@/assets/images/gears32x32.png')}
              />
            </Link>
          </View>

          <CustomDivider fullWidth={true} />

          <CustomText>Access</CustomText>

          <View style={{ flexDirection: "row", backgroundColor: "transparent", gap: 10, alignItems: "center" }}>
            <AppText>Current access: <AppText style={CustomTextStyles.highlight}>{ community.inviteonly ? "Invite Only" : "Public" }</AppText></AppText>
            {
              community.inviteonly ?
                  <Button onPress={() => changeInviteOnly(false)}>Make Public</Button>
                :
                  <Button onPress={() => changeInviteOnly(true)}>Make Invite Only</Button>
            }
          </View>

          {
            community.inviteonly && (
              <>
                <AppText style={CustomTextStyles.normalUnderline}>Invite codes</AppText>
                <InviteCodes manage_community_id={manage_community_id} initialInviteCodes={initialInviteCodes} />
              </>
            )
          }

          { community.approved && (
              <>
                <CustomDivider fullWidth={true} />
                <CustomText>Collaborations</CustomText>
                {/*<AppText>Collaborate with other Rabbls. The users of both Rabbls will see each other in results. This is free.</AppText>*/}
                {collaborations.map((collab, index) => (
                  <View key={collab.uniqueid} style={styles.collabRowWrapper}>
                    {index == 0 && <CustomDivider />}
                    <AppText>With  <CustomText>{AppUtils.getCollaborationOtherCommunity(collab, community).name}</CustomText></AppText>
                    <AppText><AppText style={AppConstants.STYLES.fontBold}>Status:</AppText> {AppUtils.getCollaborationStatusDisplay(collab)}</AppText>
                    { collab.collabstatus == CollaborationStatus.COLLABORATION_STATUS_EMAILED && <Button onPress={() => approveCollab(collab)}>Approve Collab</Button> }
                    <View style={styles.collabRow}>
                      <AppText><AppText style={AppConstants.STYLES.fontBold}>Expires:</AppText> {AppUtils.parseAndDisplayISO8601DateString(collab.expires)}</AppText>
                      <Button
                        onPress={
                          () => AppUtils.showDialog(
                                  "Cancel this collab?",
                                  () => cancelCollab(collab),
                                  "Cancel collab?",
                                  "Nevermind",
                                )
                        }
                      >Cancel Collab</Button>
                    </View>
                    <CustomDivider />
                  </View>
                ))}
                <Link href={"/manage/" + community.uniqueid + "/newcollab" as RelativePathString} asChild={AppUtilities.isWeb()}><Button>New Collaboration</Button></Link>
              </>
          )}

          <CustomDivider fullWidth={true} />
          <CustomText>Stats</CustomText>
          <RabblStats
            community={community}
            community_info={community_info}
            admin={true}
            //showMap={false}
          />
        </View>
      </Panel>
        
      <Footer />
    </BasicScrollView>
  );
}
